import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenantStore } from '@/hooks/useTenantStore';
import { format } from 'date-fns';
import { Search, FileText, Users, Building2, TrendingUp } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

interface AuditLogEntry {
  id: string;
  user_id: string | null; // null for automated actions — see actor_type
  actor_type?: 'user' | 'system';
  company_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: Record<string, any> | null;
  created_at: string;
}

const ACTION_ICONS: Record<string, any> = {
  COMPANY_CREATED: Building2,
  COMPANY_UPDATED: Building2,
  SHARE_ISSUED: TrendingUp,
  SHARE_TRANSFERRED: TrendingUp,
  USER_INVITED: Users,
  DOCUMENT_UPLOADED: FileText,
};

const ACTION_COLORS: Record<string, string> = {
  COMPANY_CREATED: 'bg-blue-500/10 text-blue-500',
  COMPANY_UPDATED: 'bg-blue-500/10 text-blue-500',
  SHARE_ISSUED: 'bg-green-500/10 text-green-500',
  SHARE_TRANSFERRED: 'bg-amber-500/10 text-amber-500',
  USER_INVITED: 'bg-purple-500/10 text-purple-500',
  DOCUMENT_UPLOADED: 'bg-cyan-500/10 text-cyan-500',
};

export default function AuditTrail() {
  const { tenantId } = useTenantStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');

  const { data: auditLogs, isLoading } = useQuery({
    queryKey: ['audit-logs', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('audit_log')
        .select('*')
        .eq('company_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      return data as AuditLogEntry[];
    },
    enabled: !!tenantId,
  });

  // Get unique actions for filter
  const uniqueActions = [...new Set(auditLogs?.map((log) => log.action) || [])];

  // Filter audit logs
  const filteredLogs = auditLogs?.filter((log) => {
    const matchesSearch =
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.actor_type === 'system' ? 'system' : (log.user_id ?? ''))
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesAction = actionFilter === 'all' || log.action === actionFilter;

    return matchesSearch && matchesAction;
  });

  const formatActionName = (action: string) => {
    return action
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getActionIcon = (action: string) => {
    const Icon = ACTION_ICONS[action] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  const getActionColor = (action: string) => {
    return ACTION_COLORS[action] || 'bg-muted text-muted-foreground';
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Audit Trail</h1>
          <p className="text-muted-foreground">
            Complete history of all actions performed in{' '}
            {tenantId ? 'this company' : 'your companies'}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              Track all user actions, transactions, and system events
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by action, entity, or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {uniqueActions.map((action) => (
                    <SelectItem key={action} value={action}>
                      {formatActionName(action)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Audit Log Table */}
            <ScrollArea className="h-[600px] rounded-md border">
              {isLoading ? (
                <div className="p-4 space-y-3">
                  {[...Array(10)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : !filteredLogs || filteredLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[400px] text-center p-4">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No audit logs found</h3>
                  <p className="text-sm text-muted-foreground">
                    {searchTerm || actionFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Start performing actions to see them logged here'}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">
                          {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`${getActionColor(log.action)} flex items-center gap-1.5 w-fit`}
                          >
                            {getActionIcon(log.action)}
                            {formatActionName(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.entity_type && (
                            <div className="space-y-1">
                              <div className="text-sm font-medium">{log.entity_type}</div>
                              {log.entity_id && (
                                <div className="text-xs text-muted-foreground font-mono">
                                  {log.entity_id.substring(0, 8)}...
                                </div>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-muted-foreground">
                            {log.user_id ? (
                              `${log.user_id.substring(0, 8)}...`
                            ) : (
                              // Named, not blanked: "system" is a fact about the
                              // row, and an empty cell reads as missing data.
                              <span className="italic">system</span>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-primary hover:underline">
                                View details
                              </summary>
                              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </details>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>

            {/* Summary Stats */}
            {filteredLogs && filteredLogs.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing{' '}
                  <span className="font-semibold text-foreground">{filteredLogs.length}</span> of{' '}
                  <span className="font-semibold text-foreground">{auditLogs?.length || 0}</span>{' '}
                  total audit entries
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
